import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
  useMemo,
} from 'react';
import {
  listActivitiesAll,
  markCommunicationsAsRead,
  assignCommunications,
  closeCommunications,
} from '../../../../../../../../services/activity';

const CommunicationsContext = createContext(null);

const DEFAULT_PAGE_SIZE = 10;

const CHANNEL_TYPES = {
  ALL: 'all',
  EMAIL: 'EMAIL',
  SMS: 'SMS',
};

const initialFilters = {
  search: '',
  channelType: CHANNEL_TYPES.ALL,
  status: 'all',
  sort: 'last_update',
};

const initialMeta = {
  total_items: 0,
  total_pages: 1,
  current_page: 1,
  per_page: DEFAULT_PAGE_SIZE,
  has_next: false,
  has_previous: false,
};

/**
 * Strip HTML tags from content
 */
const stripHtmlTags = (html) => {
  if (!html || typeof html !== 'string') return '';
  return html
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .trim();
};

/**
 * Map communication data to normalized format
 */
let commIdCounter = 0;
const mapCommunicationItem = (rawComm) => {
  const extractedId = rawComm.conversationUuid || rawComm.conversation_uuid;
  const fallbackId = `comm-${Date.now()}-${++commIdCounter}`;
  
  const rawTitle = rawComm.title || rawComm.subject || '';
  const rawDescription = rawComm.description || rawComm.lastMessage || rawComm.snippet || '';
  
  // Extract tags from status or metadata
  const tags = [];
  if (rawComm.isHandoff || rawComm.is_handoff) tags.push('Handoff');
  if (rawComm.isVip || rawComm.is_vip) tags.push('VIP');
  if (rawComm.isMissed || rawComm.is_missed) tags.push('Missed');
  if (rawComm.isCallback || rawComm.is_callback) tags.push('Callback');
  
  return {
    id: extractedId || fallbackId,
    conversationUuid: extractedId,
    title: stripHtmlTags(rawTitle),
    description: stripHtmlTags(rawDescription),
    channelType: rawComm.originChannel || rawComm.origin_channel || 'EMAIL',
    status: rawComm.status || 'ACTIVE',
    tags,
    isUnread: rawComm.isUnread || rawComm.is_unread || false,
    isLive: rawComm.isLive || rawComm.is_live || false,
    aiSummary: rawComm.aiSummary || rawComm.ai_summary || null,
    owner: {
      name: rawComm.ownerFullName || rawComm.owner_full_name || 'Unknown',
      avatar: rawComm.ownerAvatar || rawComm.owner_avatar || null,
    },
    agent: rawComm.agent ? {
      name: rawComm.agent.name || rawComm.agent.fullName || 'Unknown',
      avatar: rawComm.agent.avatar || rawComm.agent.photoUrl || null,
    } : null,
    lastMessage: rawComm.lastMessage ? {
      author: rawComm.lastMessage.author || rawComm.lastMessage.senderName || 'Customer',
      text: stripHtmlTags(rawComm.lastMessage.text || rawComm.lastMessage.content || ''),
      avatar: rawComm.lastMessage.avatar || null,
    } : null,
    messageCount: rawComm.messageCount || rawComm.message_count || 0,
    duration: rawComm.duration || null,
    additionalCount: rawComm.additionalCount || rawComm.additional_count || 0,
    startedAt: rawComm.startedAt || rawComm.started_at,
    lastUpdate: rawComm.lastUpdate || rawComm.last_update,
    createdAt: rawComm.createdAt || rawComm.created_at,
    lastUpdated: rawComm.lastUpdated || rawComm.last_updated,
    metadata: rawComm.metadata || {},
    raw: rawComm,
  };
};

export const CommunicationsProvider = ({ children, contactId }) => {
  // State
  const [communications, setCommunications] = useState([]);
  const [meta, setMeta] = useState(initialMeta);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState(initialFilters);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [selectAll, setSelectAll] = useState(false);

  // Refs for request tracking
  const fetchIdRef = useRef(0);

  // Fetch communications from API
  const fetchCommunications = useCallback(
    async (page = 1, pageSize = DEFAULT_PAGE_SIZE) => {
      if (!contactId) return;

      const requestId = ++fetchIdRef.current;
      setLoading(true);
      setError(null);

      try {
        const params = {
          party_uuid: contactId,
          page,
          page_size: pageSize,
          order_by: filters.sort || 'last_update',
          order_direction: 'DESC',
        };

        // Add channel filter if not "all"
        if (filters.channelType !== CHANNEL_TYPES.ALL) {
          params.originChannel = filters.channelType;
        }

        // Add status filter if not "all"
        if (filters.status && filters.status !== 'all') {
          params.status = filters.status;
        }

        // Add search if present
        if (filters.search?.trim()) {
          params.search = { keyword: filters.search.trim() };
        }

        const response = await listActivitiesAll(params);

        if (requestId === fetchIdRef.current) {
          // Map the response body to our normalized format
          const commBody = response.body || {};
          let items = [];

          // Handle different response structures
          if (Array.isArray(commBody)) {
            items = commBody.map(mapCommunicationItem);
          } else {
            // If response has email/sms arrays
            const emails = (commBody.email || []).map(mapCommunicationItem);
            const smsItems = (commBody.sms || []).map(mapCommunicationItem);
            items = [...emails, ...smsItems];
          }

          setCommunications(items);

          // Update meta from response
          const responseMeta = response.meta || {};
          setMeta({
            total_items: responseMeta.total_items || responseMeta.totalItems || items.length,
            total_pages: responseMeta.total_pages || responseMeta.totalPages || 1,
            current_page: responseMeta.current_page || responseMeta.currentPage || page,
            per_page: responseMeta.per_page || responseMeta.perPage || pageSize,
            has_next: responseMeta.has_next || responseMeta.hasNext || false,
            has_previous: responseMeta.has_previous || responseMeta.hasPrevious || false,
          });

          // Clear selection on data change
          setSelectedIds(new Set());
          setSelectAll(false);
        }
      } catch (err) {
        if (requestId === fetchIdRef.current) {
          console.error('Error fetching communications:', err);
          setError(err);
        }
      } finally {
        if (requestId === fetchIdRef.current) {
          setLoading(false);
        }
      }
    },
    [contactId, filters]
  );

  // Update filters
  const updateFilters = useCallback((newFilters) => {
    setFilters((prev) => ({ ...prev, ...newFilters }));
  }, []);

  // Clear all filters
  const clearFilters = useCallback(() => {
    setFilters(initialFilters);
  }, []);

  // Selection handlers
  const toggleSelect = useCallback((id) => {
    setSelectedIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
    setSelectAll(false);
  }, []);

  const toggleSelectAll = useCallback(() => {
    if (selectAll) {
      setSelectedIds(new Set());
      setSelectAll(false);
    } else {
      const allIds = new Set(communications.map((item) => item.id));
      setSelectedIds(allIds);
      setSelectAll(true);
    }
  }, [selectAll, communications]);

  const clearSelection = useCallback(() => {
    setSelectedIds(new Set());
    setSelectAll(false);
  }, []);

  // Communication action handlers
  const handleMarkAsRead = useCallback(
    async (ids) => {
      try {
        await markCommunicationsAsRead(ids);
        // Update local state to mark as read
        setCommunications((prev) =>
          prev.map((comm) =>
            ids.includes(comm.id) ? { ...comm, isUnread: false } : comm
          )
        );
        clearSelection();
        return { success: true };
      } catch (error) {
        console.error('Failed to mark as read:', error);
        return { success: false, error };
      }
    },
    [clearSelection]
  );

  const handleAssign = useCallback(
    async (ids, assigneeId = null) => {
      try {
        await assignCommunications(ids, assigneeId);
        // Refresh the list after assignment
        await fetchCommunications(meta.current_page, meta.per_page);
        clearSelection();
        return { success: true };
      } catch (error) {
        console.error('Failed to assign:', error);
        return { success: false, error };
      }
    },
    [clearSelection, fetchCommunications, meta.current_page, meta.per_page]
  );

  const handleClose = useCallback(
    async (ids) => {
      try {
        await closeCommunications(ids);
        // Update local state to mark as closed
        setCommunications((prev) =>
          prev.map((comm) =>
            ids.includes(comm.id) ? { ...comm, status: 'CLOSED' } : comm
          )
        );
        clearSelection();
        return { success: true };
      } catch (error) {
        console.error('Failed to close:', error);
        return { success: false, error };
      }
    },
    [clearSelection]
  );

  // Pagination handlers
  const handlePageChange = useCallback(
    (page) => {
      fetchCommunications(page, meta.per_page);
    },
    [fetchCommunications, meta.per_page]
  );

  const handleItemsPerPageChange = useCallback(
    (pageSize) => {
      fetchCommunications(1, pageSize);
    },
    [fetchCommunications]
  );

  // Computed values
  const hasActiveFilters = useMemo(() => {
    return (
      filters.search !== '' ||
      filters.channelType !== CHANNEL_TYPES.ALL ||
      filters.status !== 'all' ||
      filters.sort !== 'last_update'
    );
  }, [filters]);

  const selectedCount = useMemo(() => selectedIds.size, [selectedIds]);

  const value = useMemo(
    () => ({
      // State
      communications,
      meta,
      loading,
      error,
      filters,
      selectedIds,
      selectAll,
      hasActiveFilters,
      selectedCount,
      contactId,
      CHANNEL_TYPES,

      // Actions
      fetchCommunications,
      updateFilters,
      clearFilters,
      toggleSelect,
      toggleSelectAll,
      clearSelection,
      handlePageChange,
      handleItemsPerPageChange,
      handleMarkAsRead,
      handleAssign,
      handleClose,
    }),
    [
      communications,
      meta,
      loading,
      error,
      filters,
      selectedIds,
      selectAll,
      hasActiveFilters,
      selectedCount,
      contactId,
      fetchCommunications,
      updateFilters,
      clearFilters,
      toggleSelect,
      toggleSelectAll,
      clearSelection,
      handlePageChange,
      handleItemsPerPageChange,
      handleMarkAsRead,
      handleAssign,
      handleClose,
    ]
  );

  return (
    <CommunicationsContext.Provider value={value}>
      {children}
    </CommunicationsContext.Provider>
  );
};

export const useCommunications = () => {
  const context = useContext(CommunicationsContext);
  if (!context) {
    throw new Error('useCommunications must be used within CommunicationsProvider');
  }
  return context;
};

export { CHANNEL_TYPES };
export default CommunicationsContext;
